import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { QueryParam } from '@bingads-webui-react/scope-service';
import { siteMapPropType } from '@bingads-webui-campaign-react/with-site-map';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getRoutePathById, withCampaignSiteMap } from '@bingads-webui-react/with-site-map';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import { SummaryBarFactory } from './components';
import { getSummaryBarStyle } from './styles';

const PmaxAssetDetailsSummaryBarUnstyled = ({
  selectedParent,
  selectedChild,
  siteMap,
  i18n,
  scope,
  summaryBarActivityLogger,
}) => {
  const {
    aid, campaignId, cid, assetGroupId,
  } = scope.values;

  const readCampaign = useCallback(() => edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .get()
    .toPromise(), [aid, campaignId, cid]);

  const readAssetGroup = useCallback(() => edmCampaign
    .Customers.$withKey(cid)
    .Accounts.$withKey(aid)
    .Campaigns.$withKey(campaignId)
    .AssetGroups.$withKey(assetGroupId)
    .get()
    .toPromise(), [aid, assetGroupId, campaignId, cid]);

  const isListingGroup = useMemo(() => scope.location.pathname.includes('listinggroups'), [scope.location.pathname]);

  const [campaignName, setCampaignName] = useState();
  const [assetGroupName, setAssetGroupName] = useState();

  useEffect(() => {
    readCampaign().then((response) => {
      setCampaignName(response.Name);
    });
  }, [readCampaign]);

  useEffect(() => {
    if (!_.isUndefined(assetGroupId)) {
      readAssetGroup().then((response) => {
        setAssetGroupName(response.Name);
      });
    }
  }, [assetGroupId, readAssetGroup]);

  const breadcrumbsGeneralConfigs = useMemo(() => ({
    maxDisplayedItems: 3,
    items: [{
      key: 'all-campaigns',
      text: i18n.getString(_TL_('All campaigns')),
      isLongText: true,
      'aria-label': i18n.getString(_TL_('All campaigns')),
      onClick: () => {
        scope.remove(['campaignId', 'assetGroupId']);
        scope.save(getRoutePathById(siteMap, 'campaigns'));
      },
    }, {
      key: 'asset-groups',
      text: campaignName,
      isLongText: true,
      'aria-label': campaignName,
      onClick: () => {
        scope.remove(['assetGroupId']);
        scope.save(getRoutePathById(siteMap, isListingGroup ? 'listing-groups' : 'asset-groups'));
      },
    }, (selectedChild ?
      {
        key: selectedChild.id,
        text: assetGroupName,
        isCurrentItem: true,
        isLongText: true,
        'aria-label': assetGroupName,
      } : {}
    )],
  }), [assetGroupName, campaignName, i18n, isListingGroup, scope, selectedChild, siteMap]);

  const summaryBarProps = useMemo(() => ({
    i18n,
    scope,
    siteMap,
    summaryBarActivityLogger,
  }), [i18n, scope, siteMap, summaryBarActivityLogger]);

  return selectedParent && selectedChild ? (
    <SummaryBarFactory
      breadcrumbsGeneralConfigs={breadcrumbsGeneralConfigs}
      {...summaryBarProps}
    />
  ) : null;
};

PmaxAssetDetailsSummaryBarUnstyled.propTypes = {
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  selectedChild: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  siteMap: siteMapPropType.isRequired,
  summaryBarActivityLogger: PropTypes.shape().isRequired,
};

export const PmaxAssetDetailsSummaryBar = withCampaignSiteMap(withDefaultStyles(PmaxAssetDetailsSummaryBarUnstyled, getSummaryBarStyle));
